import { useCallback, useMemo, useState } from 'react';

import { type AnalyticsEventPayload, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';

import { createAtlAttributionHeader } from '../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../common/fetch/fetch-agg-response';
import { DEFAULT_GAS_CHANNEL } from '../../../constants';
import { onShownAutoDefinitionHighlights } from '../analytics';
import { KEY_PHRASES_OPERATION_NAME, KeyPhrasesQuery } from '../gql/keyphrases-query';

type JiraInputText = { text?: string; format: KnowledgeDiscoveryKeyPhraseInputTextFormat } | null;

type TenantArgs = {
	entityAri: string;
	workspaceId: string;
	product: string;
	inputText?: JiraInputText;
	fieldType?: string;
};

type JiraProductArgs = {
	jiraReporterAccountId?: string;
	jiraAssigneeAccountId?: string;
};
type AutoHighlightSupplierArgsType = TenantArgs & {
	productConfig?: JiraProductArgs;
	cloudId: string;
};

export type KeyPhraseNodeType = {
	keyPhrase: string;
	category: KeyPhraseCategory;
};

type AutohighlightSupplierState = {
	keyPhrases: KeyPhraseNodeType[];
	keyPhraseCategory: KeyPhraseCategory;
	autoHighlightRegex: RegExp;
	error: Error | undefined;
	loadAutohighlights: ({
		inputText,
	}: {
		inputText?: JiraInputText;
	}) => Promise<KeyPhraseNodeType[]>;
	resetAutohighlights: () => void;
	autohighlightsLoading: boolean;
};

export type KeyPhrasesQueryVariables = Omit<TenantArgs & JiraProductArgs, 'product'>;

export enum KnowledgeDiscoveryKeyPhraseInputTextFormat {
	PLAIN = 'PLAIN',
	ADF = 'ADF',
}

export enum KeyPhraseCategory {
	PROJECT = 'PROJECT',
	TEAM = 'TEAM',
	ACRONYM = 'ACRONYM',
	AUTO = 'AUTO',
	OTHER = 'OTHER',
}

export interface KeyPhrasesQueryType {
	knowledgeDiscovery: {
		keyPhrases: {
			nodes: KeyPhraseNodeType[];
		};
	};
}
export const AUTOHIGHLIGHTS_CACHE_KEY = 'atlassian.knowledge-discovery.autohighlights';

const escapeRegExp = (regex: string) => {
	return regex.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};

export const getHighlightRegex = (keyPhrases: KeyPhraseNodeType[]): RegExp => {
	try {
		if (!keyPhrases.length) {
			return new RegExp('(?!)');
		} else {
			return new RegExp(
				keyPhrases.map((node) => `\\b${escapeRegExp(node.keyPhrase)}\\b`).join('|'),
				'gi',
			);
		}
	} catch {
		return new RegExp('(?!)');
	}
};

const getKeyphraseCategory = (keyPhrases: KeyPhraseNodeType[]): KeyPhraseCategory => {
	return keyPhrases.length ? keyPhrases[0].category : KeyPhraseCategory.AUTO;
};

const initialState = {
	keyPhrases: [],
	keyPhraseCategory: KeyPhraseCategory.AUTO,
	autoHighlightRegex: new RegExp('(?!)'),
	loadAutohighlights: () => Promise.resolve([]),
	resetAutohighlights: () => {},
	autohighlightsLoading: false,
	error: undefined,
};

export const useAutohighlightSupplier = ({
	entityAri,
	workspaceId,
	product,
	productConfig,
	fieldType,
	cloudId,
}: AutoHighlightSupplierArgsType) => {
	const [state, setState] = useState<AutohighlightSupplierState>(initialState);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const fireAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload) => {
			createAnalyticsEvent(payload).fire(DEFAULT_GAS_CHANNEL);
		},
		[createAnalyticsEvent],
	);

	const { jiraReporterAccountId, jiraAssigneeAccountId } = productConfig || {};

	const loadAutohighlights = useCallback(
		async ({ inputText }: { inputText?: JiraInputText }) => {
			try {
				setState({
					...initialState,
					autohighlightsLoading: true,
				});

				const keyPhrasesResult = await fetchAggResponse<
					KeyPhrasesQueryVariables,
					KeyPhrasesQueryType
				>({
					graphQuery: KeyPhrasesQuery,
					variables: {
						workspaceId,
						entityAri,
						...(!!jiraReporterAccountId && { jiraReporterAccountId }),
						...(!!jiraAssigneeAccountId && { jiraAssigneeAccountId }),
						...(!!inputText && { inputText }),
					},
					operationName: KEY_PHRASES_OPERATION_NAME,
					headers: createAtlAttributionHeader({
						atlWorkspaceId: workspaceId,
						tenantId: cloudId,
						product,
					}),
				});
				const keyPhrases = keyPhrasesResult?.data?.knowledgeDiscovery?.keyPhrases?.nodes
					? keyPhrasesResult.data.knowledgeDiscovery.keyPhrases.nodes
					: [];

				if (keyPhrases.length > 0 && fg('kd_definitions_click_and_show_event_product_parity')) {
					fireAnalyticsEvent(
						onShownAutoDefinitionHighlights({
							product,
							contentId: entityAri,
							keyPhrases,
							fieldType,
						}),
					);
				}

				const autoHighlightRegex = getHighlightRegex(keyPhrases);
				setState({
					...initialState,
					keyPhraseCategory: getKeyphraseCategory(keyPhrases),
					autoHighlightRegex,
					keyPhrases,
				});

				return keyPhrases;
			} catch (error: any) {
				setState({
					...initialState,
					error: error.message,
				});
				return [];
			}
		},
		[
			entityAri,
			workspaceId,
			fireAnalyticsEvent,
			product,
			jiraReporterAccountId,
			jiraAssigneeAccountId,
			fieldType,
			cloudId,
		],
	);

	const resetAutohighlights = useCallback((): void => {
		setState(initialState);
	}, []);

	const autoHighlightRegex = useMemo(() => state.autoHighlightRegex, [state.autoHighlightRegex]);

	const autohighlightsLoading = useMemo(
		() => state.autohighlightsLoading,
		[state.autohighlightsLoading],
	);

	const keyPhrases = useMemo(() => state.keyPhrases, [state.keyPhrases]);

	const error = useMemo(() => state.error, [state.error]);

	return {
		keyPhrases,
		keyPhraseCategory: state.keyPhraseCategory,
		autoHighlightRegex,
		loadAutohighlights,
		resetAutohighlights,
		autohighlightsLoading,
		error,
	};
};
